import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertCircle, CalendarClock, ChevronDown, Clock, MapPin, Navigation, Phone, Send, X } from "lucide-react";
import "./PatientChatsHubBidBookingPanel.scss";
import { apiRequest } from "../../Api/Api";
import { ENDPOINTS } from "../../Api/Endpoints";
import {
  fmtINR,
  extractMongoId,
  toDisplayAmount,
  pickRowForBooking,
  procedureLabelFromBidLogRow,
} from "./PatientChatShared";
import {
  appointmentClinicLabel,
  appointmentTimePart,
  bookingSlotMapKey,
  slotClinicLabel,
  slotTimePart,
} from "../../utils/timeSlotDisplay";
import {
  BID_AMOUNT_CONTACT_BLOCKED_MSG,
  bidAmountDigitsOnly,
  bidAmountInputToNumber,
  bidAmountRawHasDisallowedSymbols,
  formatIndianBidAmountInput,
  validateBidAmountDigitsNotPhoneLike,
} from "../../utils/bidAmountInput";
import { patientPendingAppointmentLeadMessage } from "../../utils/appointmentPatientStatusCopy";

function rowTime(b) {
  const t = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
  return Number.isFinite(t) ? t : 0;
}

function pickLatest(rows) {
  if (!Array.isArray(rows) || !rows.length) return null;
  return rows.reduce((best, b) => (rowTime(b) >= rowTime(best) ? b : best));
}

function haversineKm(lat1, lon1, lat2, lon2) {
  if (![lat1, lon1, lat2, lon2].every((x) => Number.isFinite(Number(x)))) return null;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const a1 = toRad(Number(lat1));
  const a2 = toRad(Number(lat2));
  const dLat = toRad(Number(lat2) - Number(lat1));
  const dLon = toRad(Number(lon2) - Number(lon1));
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(a1) * Math.cos(a2) * Math.sin(dLon / 2) ** 2;
  return R * (2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h)));
}

/**
 * Provider profiles sometimes save Kolkata-ish coords reversed (lat/long swapped).
 * India mainland: lat ~6–37°N, lng ~68–98°E.
 */
function correctLikelySwappedIndiaCoords(lat, lng) {
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  const latLooksIndia = (x) => x >= 6 && x <= 37;
  const lngLooksIndia = (x) => x >= 68 && x <= 98;
  if (latLooksIndia(a) && lngLooksIndia(b)) return { lat: a, lng: b };
  if (latLooksIndia(b) && lngLooksIndia(a)) return { lat: b, lng: a };
  return { lat: a, lng: b };
}

/** Straight-line distance for UI (km); metres under 1 km. */
function formatDistanceKm(km) {
  if (km == null || !Number.isFinite(km)) return null;
  if (km < 1) return `~${Math.max(1, Math.round(km * 1000))} m`;
  if (km < 10) return `~${km.toFixed(1)} km`;
  return `~${Math.round(km)} km`;
}

function normalizeGeocodeKey(query) {
  return (
    "in2|" +
    String(query || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ")
      .slice(0, 320)
  );
}

/** Best-effort address string for lookup when API coords are missing. */
function slotGeocodeQuery(s) {
  const addr = String(s?.clinicAddress || "").trim();
  const clinic = slotClinicLabel(s);
  let q = addr.length >= 10 ? addr : addr && clinic ? `${clinic}, ${addr}` : clinic || addr || "";
  if (!q) return "";
  const lower = q.toLowerCase();
  const hasCountryHint =
    /\bindia\b/.test(lower) ||
    /\bbharat\b/.test(lower) ||
    /\bwest bengal\b/.test(lower) ||
    /\bkolkata\b/.test(lower) ||
    /\bchennai\b/.test(lower) ||
    /\bmumbai\b/.test(lower) ||
    /\bdelhi\b/.test(lower) ||
    /\bbangalore\b/i.test(q) ||
    /\bbengaluru\b/i.test(q);
  if (!hasCountryHint) q = `${q}, India`;
  return q;
}

/**
 * True when the same rounded pin is reused for rows that resolve to different address-search keys
 * (ambiguous venue data). Multiple time slots at one clinic with one address share one key — then
 * we keep provider/API coords and distances work without waiting on OSM.
 */
function slotsNeedPerAddressGeocode(slots) {
  if (!Array.isArray(slots) || slots.length < 2) return false;
  const coordCounts = new Map();
  const geocodeKeysByCoord = new Map();
  for (const s of slots) {
    const lat = Number(s?.clinicLatitude);
    const lng = Number(s?.clinicLongitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return true;
    const ck = `${lat.toFixed(4)},${lng.toFixed(4)}`;
    coordCounts.set(ck, (coordCounts.get(ck) || 0) + 1);
    const q = slotGeocodeQuery(s);
    const gk = q ? normalizeGeocodeKey(q) : "";
    if (!geocodeKeysByCoord.has(ck)) geocodeKeysByCoord.set(ck, new Set());
    if (gk) geocodeKeysByCoord.get(ck).add(gk);
  }
  for (const [ck, count] of coordCounts) {
    if (count < 2) continue;
    const distinct = geocodeKeysByCoord.get(ck);
    if (distinct && distinct.size >= 2) return true;
  }
  return false;
}

function apiCoordsForSlot(s) {
  const lat = Number(s?.clinicLatitude);
  const lng = Number(s?.clinicLongitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const fixed = correctLikelySwappedIndiaCoords(lat, lng);
  return fixed || { lat, lng };
}

/**
 * Prefer per-address geocode when rows share one pin or when we have a full address line.
 * While that geocode is pending, return null so every row does not show the same wrong km.
 */
function slotCoordsForDistance(s, geocodeCache, needsPerAddressGeocode) {
  const q = slotGeocodeQuery(s);
  const k = q ? normalizeGeocodeKey(q) : "";
  const addrLen = String(s?.clinicAddress || "").trim().length;
  const clinicLab = slotClinicLabel(s);
  const clinicLen = String(clinicLab || "").trim().length;
  const preferAddress =
    Boolean(k) && (addrLen >= 5 || clinicLen >= 12 || needsPerAddressGeocode);
  const cache = geocodeCache && typeof geocodeCache === "object" ? geocodeCache : null;

  if (preferAddress && cache && Object.prototype.hasOwnProperty.call(cache, k)) {
    const hit = cache[k];
    if (hit && Number.isFinite(hit.lat) && Number.isFinite(hit.lng)) return { lat: hit.lat, lng: hit.lng };
  } else if (preferAddress && cache && !Object.prototype.hasOwnProperty.call(cache, k)) {
    return null;
  }

  const api = apiCoordsForSlot(s);
  if (api) return api;
  if (cache && k && cache[k] && Number.isFinite(cache[k].lat) && Number.isFinite(cache[k].lng)) return cache[k];
  return null;
}

function distanceKmFromPatient(s, patientGeo, geocodeCache, needsPerAddressGeocode) {
  if (!patientGeo || !Number.isFinite(patientGeo.lat) || !Number.isFinite(patientGeo.lng)) return null;
  const c = slotCoordsForDistance(s, geocodeCache, needsPerAddressGeocode);
  if (!c) return null;
  return haversineKm(patientGeo.lat, patientGeo.lng, c.lat, c.lng);
}

function compareSlotsByDateTime(a, b) {
  const da = String(a.appointmentDate || "").localeCompare(String(b.appointmentDate || ""));
  if (da !== 0) return da;
  return String(a.timeSlot || "").localeCompare(String(b.timeSlot || ""));
}

function slotMapsUrl(s, geocodeCache, needsPerAddressGeocode) {
  const c = slotCoordsForDistance(s, geocodeCache, needsPerAddressGeocode);
  if (c && Number.isFinite(c.lat) && Number.isFinite(c.lng)) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${c.lat},${c.lng}`)}`;
  }
  const q = [String(s?.clinicAddress || "").trim(), slotClinicLabel(s)].filter(Boolean).join(" · ");
  if (!q) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

/** Maps link for a saved appointment row (uses venue fields from API when present). */
function appointmentMapsUrl(appt) {
  if (!appt) return null;
  const lat = Number(appt.clinicLatitude);
  const lng = Number(appt.clinicLongitude);
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    const c = correctLikelySwappedIndiaCoords(lat, lng);
    const pt = c || { lat, lng };
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${pt.lat},${pt.lng}`)}`;
  }
  const q = [String(appt.clinicAddress || "").trim(), appointmentClinicLabel(appt)].filter(Boolean).join(" · ");
  if (!q) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

function telHref(phone) {
  const raw = String(phone || "").trim();
  if (!raw) return null;
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 7) return null;
  const hasPlus = raw.includes("+");
  const core = hasPlus ? `+${digits.replace(/^\+/, "")}` : digits;
  return `tel:${core}`;
}

/** `appointmentDate` from API is usually `YYYY-MM-DD` — parse at noon to avoid TZ day shift. */
function formatSlotCalendar(appointmentDateStr) {
  const raw = String(appointmentDateStr || "").trim();
  if (!raw) return "";
  const d = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? new Date(`${raw}T12:00:00`) : new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Only rewrite messages from the old slot-lock flow (substring "hold" alone would false-positive, e.g. "placeholder"). */
function sanitizeBookingPanelError(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  const t = s.toLowerCase();
  const legacyHoldCopy =
    /select a slot to hold|hold it for|hold may have|your hold|slot to hold|5\s*minutes?,\s*then\s*confirm/.test(
      t
    );
  if (legacyHoldCopy) {
    return "Choose a time from the list, then tap Book this visit. If something went wrong, choose the slot again.";
  }
  return s;
}

/** Rows where the patient still has to accept/decline (or counter on a reverse bid). */
function needsPatientRow(b) {
  return (
    b &&
    String(b.patientAction) === "pending" &&
    ["countered", "accepted_patient_price"].includes(String(b.providerAction || ""))
  );
}

/**
 * One conversation can include several BidLog rows (e.g. multiple subcategories).
 * Prefer the reverse-bid row when several are pending; then latest by activity time.
 */
function pickRowForPatientActions(bidHistory) {
  if (!Array.isArray(bidHistory) || !bidHistory.length) return null;
  const pending = bidHistory.filter(needsPatientRow);
  if (!pending.length) return bidHistory[bidHistory.length - 1];

  const reversePending = pending.filter(
    (b) => String(b.providerAction) === "countered" && b.reverseBidAmount != null
  );
  const pool = reversePending.length ? reversePending : pending;
  return pickLatest(pool);
}

/** Agreed visit price for the booking row (matches offer copy in this panel). */
function agreedDisplayAmountForBooking(b) {
  if (!b) return null;
  const pr = String(b.providerAction || "");
  const pa = String(b.patientAction || "");
  if (pr === "accepted_patient_price") return toDisplayAmount(b.patientBidPrice);
  if (pr === "countered" && pa === "accepted") return toDisplayAmount(b.reverseBidAmount);
  return null;
}

export default function PatientChatsHubBidBookingPanel({
  variant = "default",
  bidHistory,
  appointment,
  /** From GET …/messages — opening match window passed with no provider reply. */
  openingBidExpired = false,
  providerLabel,
  onRefresh,
  inlineError,
  setInlineError,
  /** When false (e.g. mobile chat list without thread open), do not auto-open or show the bottom booking sheet. */
  bookingUiInFocusedThread = true,
  /** If user taps "Choose visit slot" while on mobile list, open the thread first so the sheet can appear. */
  onRequestOpenMobileThread,
}) {
  const isCompact = variant === "compact";
  const actionBid = useMemo(() => pickRowForPatientActions(bidHistory), [bidHistory]);
  const bookingBid = useMemo(() => pickRowForBooking(bidHistory), [bidHistory]);

  const needsPatientResponse = Boolean(
    actionBid &&
      actionBid.patientAction === "pending" &&
      ["countered", "accepted_patient_price"].includes(String(actionBid.providerAction || ""))
  );

  const respondBidLogId = useMemo(() => {
    if (!needsPatientResponse || !actionBid) return "";
    return extractMongoId(actionBid.bidLogId ?? actionBid._id ?? actionBid.id ?? actionBid.bid_log_id);
  }, [needsPatientResponse, actionBid]);

  const bookBidLogId = useMemo(() => {
    if (!bookingBid) return "";
    return extractMongoId(bookingBid.bidLogId ?? bookingBid._id ?? bookingBid.id ?? bookingBid.bid_log_id);
  }, [bookingBid]);

  const [busy, setBusy] = useState(false);
  const [counter, setCounter] = useState("");
  const [bookOpen, setBookOpen] = useState(false);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotConfirmOpen, setSlotConfirmOpen] = useState(null);
  const [patientGeo, setPatientGeo] = useState(null);
  const [geoStatus, setGeoStatus] = useState("idle");
  const [savedLocationLabel, setSavedLocationLabel] = useState("");
  const [locationSaving, setLocationSaving] = useState(false);
  const [locationSaveError, setLocationSaveError] = useState("");
  /** When clinic coords are missing on slots, cache Google Geocode results by normalized query (requires Maps key). */
  const [geocodeCache, setGeocodeCache] = useState({});
  const geocodeCacheRef = useRef(geocodeCache);
  geocodeCacheRef.current = geocodeCache;
  const geocodeInFlightRef = useRef(new Set());
  /** `nearest` needs patient location; `soonest` is date/time order from the schedule. */
  const [slotSort, setSlotSort] = useState("soonest");
  const suggestedNearestAfterGeoRef = useRef(false);
  const prevBookable = useRef(false);
  /** Ignore sheet backdrop closes briefly after opening the slot modal (avoids the confirm tap closing the sheet underneath). */
  const sheetBackdropSuppressUntilRef = useRef(0);
  const [narrowBookSheet, setNarrowBookSheet] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 1023px)").matches : false
  );
  const [visitSectionOpen, setVisitSectionOpen] = useState(true);
  const visitSummaryId = useId();
  const visitDetailsId = useId();

  const showCounter =
    actionBid?.providerAction === "countered" && String(actionBid?.patientAction) === "pending";

  const canBook =
    bookingBid &&
    (bookingBid.providerAction === "accepted_patient_price" ||
      (bookingBid.providerAction === "countered" && bookingBid.patientAction === "accepted")) &&
    !appointment;

  const agreedPriceLabel = useMemo(() => fmtINR(agreedDisplayAmountForBooking(bookingBid)), [bookingBid]);

  const procedureLabel = useMemo(() => procedureLabelFromBidLogRow(bookingBid), [bookingBid]);

  const appointmentStatus = String(appointment?.status || "").toLowerCase();

  const canChangePendingSlot = Boolean(
    appointment &&
      appointmentStatus === "pending_approval" &&
      String(appointment.doctorAction || "").toLowerCase() === "pending"
  );

  const loadSlots = useCallback(async () => {
    if (!bookBidLogId) return;
    setSlotsLoading(true);
    setInlineError("");
    try {
      const res = await apiRequest({
        method: "GET",
        url: ENDPOINTS.GET_BID_BOOKING_SLOTS(bookBidLogId),
      });
      const list = Array.isArray(res?.slots) ? res.slots : [];
      setSlots(list);
    } catch (e) {
      setSlots([]);
      setInlineError(sanitizeBookingPanelError(e?.message) || "Could not load available times.");
    } finally {
      setSlotsLoading(false);
    }
  }, [bookBidLogId, setInlineError]);

  useEffect(() => {
    if (!bookBidLogId || !bookOpen) return;
    if (!((canBook && !appointment) || canChangePendingSlot)) return;
    void loadSlots();
  }, [bookBidLogId, bookOpen, canBook, appointment, canChangePendingSlot, loadSlots]);

  useEffect(() => {
    if (!appointment) return;
    setSlotConfirmOpen(null);
    setBookOpen(false);
    /** Stops the “book visit” sheet from auto-opening again after a visit exists (avoids flicker if props lag). */
    prevBookable.current = true;
  }, [appointment]);

  useEffect(() => {
    if (!bookingUiInFocusedThread) {
      prevBookable.current = false;
      return;
    }
    if (appointment) return;
    const ready = Boolean(canBook && bookBidLogId);
    if (ready && !prevBookable.current) setBookOpen(true);
    prevBookable.current = ready;
  }, [canBook, appointment, bookBidLogId, bookingUiInFocusedThread]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const fn = () => setNarrowBookSheet(mq.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  const bookingSheetActive = Boolean(
    bookOpen &&
      narrowBookSheet &&
      bookingUiInFocusedThread &&
      ((canBook && !appointment) || canChangePendingSlot)
  );

  useEffect(() => {
    if (!bookingSheetActive) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [bookingSheetActive]);

  useEffect(() => {
    if (patientGeo && Number.isFinite(patientGeo.lat) && Number.isFinite(patientGeo.lng)) {
      if (!suggestedNearestAfterGeoRef.current) {
        suggestedNearestAfterGeoRef.current = true;
        setSlotSort("nearest");
      }
    } else {
      suggestedNearestAfterGeoRef.current = false;
      setSlotSort((prev) => (prev === "nearest" ? "soonest" : prev));
    }
  }, [patientGeo]);

  const needsPerAddressGeocode = useMemo(() => slotsNeedPerAddressGeocode(slots), [slots]);

  useEffect(() => {
    if (!patientGeo || !Number.isFinite(patientGeo.lat) || !Number.isFinite(patientGeo.lng)) return;
    if (!Array.isArray(slots) || !slots.length) return;

    const perAddr = slotsNeedPerAddressGeocode(slots);
    const todo = [];
    const seen = new Set();
    for (const s of slots) {
      const q = slotGeocodeQuery(s);
      if (!q) continue;
      const k = normalizeGeocodeKey(q);
      if (!k || seen.has(k)) continue;
      const plat = Number(s?.clinicLatitude);
      const plng = Number(s?.clinicLongitude);
      const hasApi = Number.isFinite(plat) && Number.isFinite(plng);
      const addrLen = String(s?.clinicAddress || "").trim().length;
      const clinicLab = slotClinicLabel(s);
      const clinicLen = String(clinicLab || "").trim().length;
      const shouldFetch = !hasApi || addrLen >= 5 || clinicLen >= 12 || perAddr;
      if (!shouldFetch) continue;
      seen.add(k);
      if (k in geocodeCacheRef.current) continue;
      if (geocodeInFlightRef.current.has(k)) continue;
      todo.push({ k, q });
    }
    if (!todo.length) return;

    let cancelled = false;
    for (const { k } of todo) geocodeInFlightRef.current.add(k);

    void (async () => {
      const updates = {};
      let i = 0;
      for (const { k, q } of todo) {
        if (cancelled) break;
        if (i++ > 0) await new Promise((r) => setTimeout(r, 400));
        try {
          const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&countrycodes=in&q=${encodeURIComponent(
            q
          )}`;
          const res = await fetch(url, {
            headers: { Accept: "application/json" },
          });
          const data = await res.json();
          const hit = Array.isArray(data) ? data[0] : null;
          const lat = hit ? Number(hit.lat) : NaN;
          const lng = hit ? Number(hit.lon) : NaN;
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            updates[k] = { lat, lng };
          } else {
            updates[k] = null;
          }
        } catch {
          updates[k] = null;
        } finally {
          geocodeInFlightRef.current.delete(k);
        }
      }
      if (!cancelled && Object.keys(updates).length) {
        setGeocodeCache((prev) => ({ ...prev, ...updates }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [patientGeo, slots]);

  const sortedSlots = useMemo(() => {
    if (!Array.isArray(slots) || !slots.length) return [];
    const patientReady =
      patientGeo && Number.isFinite(patientGeo.lat) && Number.isFinite(patientGeo.lng);
    const wantNearest = slotSort === "nearest" && patientReady;

    if (!wantNearest) {
      return [...slots].sort(compareSlotsByDateTime);
    }

    const decorated = slots.map((s) => ({
      s,
      km: distanceKmFromPatient(s, patientGeo, geocodeCache, needsPerAddressGeocode),
    }));
    const anyDist = decorated.some((x) => x.km != null);
    if (!anyDist) {
      return [...slots].sort(compareSlotsByDateTime);
    }
    decorated.sort((a, b) => {
      if (a.km == null && b.km == null) return compareSlotsByDateTime(a.s, b.s);
      if (a.km == null) return 1;
      if (b.km == null) return -1;
      if (a.km !== b.km) return a.km - b.km;
      return compareSlotsByDateTime(a.s, b.s);
    });
    return decorated.map((x) => x.s);
  }, [slots, patientGeo, slotSort, geocodeCache, needsPerAddressGeocode]);

  const requestLocation = useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setGeoStatus("error");
      setInlineError("This browser does not support location.");
      return;
    }
    setGeoStatus("loading");
    setInlineError("");
    setLocationSaveError("");
    setSavedLocationLabel("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setPatientGeo({ lat, lng });
        setGeoStatus("ok");
        setLocationSaving(true);
        void (async () => {
          try {
            const res = await apiRequest({
              method: "POST",
              url: ENDPOINTS.SAVE_PATIENT_LOCATION,
              data: { latitude: lat, longitude: lng },
            });
            const display = String(res?.location?.display || "").trim();
            setSavedLocationLabel(display);
            setLocationSaveError("");
          } catch (e) {
            setLocationSaveError(e?.message || "Could not save location to your profile.");
            setSavedLocationLabel("");
          } finally {
            setLocationSaving(false);
          }
        })();
      },
      () => {
        setGeoStatus("denied");
        setLocationSaving(false);
        setInlineError(
          "Location unavailable. Enable location permission to sort clinics by distance, or browse the list below."
        );
      },
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 12_000 }
    );
  }, [setInlineError]);

  const onAcceptOffer = async () => {
    if (!respondBidLogId) {
      setInlineError("Could not link this offer to a bid. Refresh the page or reopen this chat.");
      return;
    }
    setBusy(true);
    setInlineError("");
    try {
      await apiRequest({ method: "POST", url: ENDPOINTS.RESPOND_TO_BID(respondBidLogId), data: { action: "accept" } });
      await onRefresh?.();
    } catch (e) {
      setInlineError(e?.message || "Could not accept.");
    } finally {
      setBusy(false);
    }
  };

  const onDeclineOffer = async () => {
    if (!respondBidLogId) {
      setInlineError("Could not link this offer to a bid. Refresh the page or reopen this chat.");
      return;
    }
    setBusy(true);
    setInlineError("");
    try {
      await apiRequest({ method: "POST", url: ENDPOINTS.RESPOND_TO_BID(respondBidLogId), data: { action: "reject" } });
      await onRefresh?.();
    } catch (e) {
      setInlineError(e?.message || "Could not decline.");
    } finally {
      setBusy(false);
    }
  };

  const onCounterFieldChange = (raw) => {
    if (bidAmountRawHasDisallowedSymbols(raw)) {
      setInlineError(BID_AMOUNT_CONTACT_BLOCKED_MSG);
      return;
    }
    setInlineError("");
    setCounter(formatIndianBidAmountInput(raw));
  };

  const onSubmitCounter = async () => {
    if (!respondBidLogId) {
      setInlineError("Could not link this offer to a bid. Refresh the page or reopen this chat.");
      return;
    }
    const digits = bidAmountDigitsOnly(counter);
    const phoneLike = validateBidAmountDigitsNotPhoneLike(digits);
    if (phoneLike) {
      setInlineError(phoneLike);
      return;
    }
    const n = bidAmountInputToNumber(counter);
    if (!Number.isFinite(n) || n <= 0 || n > Number.MAX_SAFE_INTEGER) {
      setInlineError("Enter a valid counter amount.");
      return;
    }
    setBusy(true);
    setInlineError("");
    try {
      await apiRequest({ method: "POST", url: ENDPOINTS.PATIENT_COUNTER_BID(respondBidLogId), data: { amount: n } });
      setCounter("");
      await onRefresh?.();
    } catch (e) {
      setInlineError(e?.message || "Could not send counter-offer.");
    } finally {
      setBusy(false);
    }
  };

  const onConfirmBooking = useCallback(
    async (sel) => {
      if (!bookBidLogId || !sel?.appointmentDate || !sel?.timeSlot) {
        setInlineError("Choose a visit time, then tap Book this visit.");
        return;
      }
      setBusy(true);
      setInlineError("");
      try {
        if (canChangePendingSlot && appointment?._id) {
          const apptId = extractMongoId(appointment._id);
          if (!apptId) {
            setInlineError("Could not read appointment id. Refresh and try again.");
            return;
          }
          await apiRequest({
            method: "PATCH",
            url: ENDPOINTS.UPDATE_PENDING_APPOINTMENT_SLOT(apptId),
            data: {
              appointmentDate: sel.appointmentDate,
              timeSlot: sel.timeSlot,
            },
          });
        } else {
          await apiRequest({
            method: "POST",
            url: ENDPOINTS.BOOK_APPOINTMENT,
            data: {
              bidId: bookBidLogId,
              appointmentDate: sel.appointmentDate,
              timeSlot: sel.timeSlot,
            },
          });
        }
        setSlotConfirmOpen(null);
        setBookOpen(false);
        prevBookable.current = false;
        await onRefresh?.();
      } catch (e) {
        setInlineError(sanitizeBookingPanelError(e?.message) || "Booking failed.");
      } finally {
        setBusy(false);
      }
    },
    [bookBidLogId, canChangePendingSlot, appointment, setInlineError, onRefresh]
  );

  const onConfirmBookFromModal = async () => {
    const slot = slotConfirmOpen;
    if (!slot) return;
    sheetBackdropSuppressUntilRef.current = Date.now() + 400;
    await onConfirmBooking({
      appointmentDate: slot.appointmentDate,
      timeSlot: slot.timeSlot,
    });
    requestAnimationFrame(() => setSlotConfirmOpen(null));
  };

  const onCloseBooking = useCallback(() => {
    setSlotConfirmOpen(null);
    setBookOpen(false);
    setInlineError("");
  }, [setInlineError]);

  /** Collapse the booking sheet when leaving the thread on mobile (narrow layout). */
  useEffect(() => {
    if (bookingUiInFocusedThread || !bookOpen || !narrowBookSheet) return;
    setBookOpen(false);
    setSlotConfirmOpen(null);
  }, [bookingUiInFocusedThread, bookOpen, narrowBookSheet]);

  useEffect(() => {
    if (!bookingSheetActive) return;
    const onKey = (e) => {
      if (e.key !== "Escape" || busy) return;
      e.preventDefault();
      if (slotConfirmOpen) {
        setSlotConfirmOpen(null);
        return;
      }
      void onCloseBooking();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [bookingSheetActive, busy, onCloseBooking, slotConfirmOpen]);

  const anchorBid = actionBid || bookingBid;
  const statusBid = useMemo(() => pickLatest(bidHistory), [bidHistory]);

  const waitingOpeningBid =
    statusBid &&
    String(statusBid.providerAction) === "pending" &&
    String(statusBid.patientAction) === "pending";

  const waitingAfterPatientCounter =
    statusBid &&
    String(statusBid.patientAction) === "countered" &&
    String(statusBid.providerAction) === "pending";

  if (!anchorBid && !appointment) {
    return (
      <div className={`pch-bid-panel pch-bid-panel--notice${isCompact ? " pch-bid-panel--compact" : ""}`}>
        <p className="pch-bid-panel__muted" style={{ margin: 0 }}>
          No bid rows are linked to this chat yet. Open this thread from your care pricing flow, or refresh if you just
          started negotiating.
        </p>
      </div>
    );
  }

  const confirmSlot = slotConfirmOpen;
  const confirmMaps = confirmSlot ? slotMapsUrl(confirmSlot, geocodeCache, needsPerAddressGeocode) : null;
  const confirmPhone = confirmSlot ? String(confirmSlot.clinicPhone || "").trim() : "";
  const confirmTel = confirmPhone ? telHref(confirmPhone) : null;
  const confirmClinic = confirmSlot ? slotClinicLabel(confirmSlot) : "";
  const confirmDistKm =
    confirmSlot && patientGeo && Number.isFinite(patientGeo.lat) && Number.isFinite(patientGeo.lng)
      ? distanceKmFromPatient(confirmSlot, patientGeo, geocodeCache, needsPerAddressGeocode)
      : null;
  const confirmDistLabel = formatDistanceKm(confirmDistKm);

  const visitMapsUrl = appointment ? appointmentMapsUrl(appointment) : null;
  const visitPhone = appointment ? String(appointment.clinicPhone || "").trim() : "";
  const visitTel = telHref(visitPhone);

  const renderBookingVisitInner = () => (
    <>
      {inlineError && bookingSheetActive ? (
        <p className="pch-bid-panel__err pch-book-sheet__inline-err" role="alert">
          {inlineError}
        </p>
      ) : null}
      <div className="pch-bid-panel__book-head">
        <span {...(bookingSheetActive ? { id: "pch-book-sheet-title" } : {})}>
          {canChangePendingSlot ? "Change your visit time" : "Book your visit"}
        </span>
        <button type="button" className="pch-bid-panel__link" onClick={() => void onCloseBooking()}>
          Close
        </button>
      </div>
      <div className="pch-bid-panel__loc-card">
        <div className="pch-bid-panel__loc-card-head">
          <Navigation size={18} strokeWidth={2} aria-hidden />
          <div>
            <p className="pch-bid-panel__loc-title">Your location</p>
            <p className="pch-bid-panel__loc-desc">
              Share approximate location to show <strong>distance to each clinic</strong> and sort by nearest. We use it
              here to sort this list and run an optional address lookup when a clinic has no map pin yet.
            </p>
          </div>
        </div>
        <div className="pch-bid-panel__loc-actions">
          <button
            type="button"
            className="pch-bid-panel__btn pch-bid-panel__btn--primary"
            disabled={geoStatus === "loading"}
            onClick={() => void requestLocation()}
          >
            {geoStatus === "loading" ? "Locating…" : patientGeo ? "Update location" : "Use my location"}
          </button>
          {patientGeo ? (
            <button
              type="button"
              className="pch-bid-panel__btn"
              onClick={() => {
                setPatientGeo(null);
                setGeoStatus("idle");
                setSavedLocationLabel("");
                setLocationSaveError("");
                setLocationSaving(false);
                setGeocodeCache({});
                geocodeInFlightRef.current.clear();
              }}
            >
              Clear
            </button>
          ) : null}
        </div>
        {geoStatus === "ok" ? (
          <div className="pch-bid-panel__loc-foot">
            <p className="pch-bid-panel__loc-ok">
              Location on — distances are straight-line (not driving time). Use <strong>Sort → Nearest clinic</strong>{" "}
              when coordinates or addresses can be matched. Address lookup uses{" "}
              <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">
                OpenStreetMap
              </a>{" "}
              when a slot has no pin yet.
            </p>
            {locationSaving ? <p className="pch-bid-panel__loc-muted">Saving location to your profile…</p> : null}
            {savedLocationLabel ? (
              <p className="pch-bid-panel__loc-saved">
                Saved as: <strong>{savedLocationLabel}</strong>
              </p>
            ) : null}
            {locationSaveError ? (
              <p className="pch-bid-panel__loc-save-err" role="status">
                {locationSaveError}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
      {slotsLoading ? <p className="pch-bid-panel__muted">Loading available times…</p> : null}
      {!slotsLoading && slots.length === 0 ? (
        <p className="pch-bid-panel__muted">
          No published slots yet. When your {providerLabel.toLowerCase()} adds availability, it will show up here.
        </p>
      ) : null}
      {!slotsLoading && slots.length > 0 ? (
        <div className="pch-bid-panel__slot-sort">
          <label htmlFor="pch-slot-sort" className="pch-bid-panel__slot-sort-label">
            Sort by
          </label>
          <select
            id="pch-slot-sort"
            className="pch-bid-panel__slot-sort-select"
            value={slotSort}
            onChange={(e) => setSlotSort(e.target.value)}
          >
            <option
              value="nearest"
              disabled={
                !patientGeo || !Number.isFinite(patientGeo.lat) || !Number.isFinite(patientGeo.lng)
              }
            >
              Nearest clinic
            </option>
            <option value="soonest">Soonest appointment</option>
          </select>
        </div>
      ) : null}
      {slotSort === "nearest" &&
      patientGeo &&
      Number.isFinite(patientGeo.lat) &&
      sortedSlots.length > 0 &&
      !sortedSlots.some(
        (s) => distanceKmFromPatient(s, patientGeo, geocodeCache, needsPerAddressGeocode) != null
      ) ? (
        <p className="pch-bid-panel__slot-sort-warn" role="status">
          Distances unavailable until clinics have coordinates on file or addresses can be matched. Showing soonest
          times instead.
        </p>
      ) : null}
      <ul className="pch-bid-panel__slots">
        {sortedSlots.map((s) => {
          const key = `${s.appointmentDate}|${s.timeSlot}`;
          const active =
            slotConfirmOpen &&
            bookingSlotMapKey(slotConfirmOpen.appointmentDate, slotConfirmOpen.timeSlot) ===
              bookingSlotMapKey(s.appointmentDate, s.timeSlot);
          const clinic = slotClinicLabel(s);
          const maps = slotMapsUrl(s, geocodeCache, needsPerAddressGeocode);
          const phone = String(s.clinicPhone || "").trim();
          const tel = telHref(phone);
          const distKm =
            patientGeo && Number.isFinite(patientGeo.lat) && Number.isFinite(patientGeo.lng)
              ? distanceKmFromPatient(s, patientGeo, geocodeCache, needsPerAddressGeocode)
              : null;
          const distLabel = formatDistanceKm(distKm);
          return (
            <li key={key}>
              <div className={`pch-bid-panel__slot-card${active ? " is-active" : ""}`}>
                <button
                  type="button"
                  className="pch-bid-panel__slot-main"
                  disabled={busy}
                  onClick={() => setSlotConfirmOpen(s)}
                >
                  <span className="pch-bid-panel__slot-day">
                    {formatSlotCalendar(s.appointmentDate) || s.dayName || s.appointmentDate}
                  </span>
                  <span className="pch-bid-panel__slot-time">{slotTimePart(s)}</span>
                  {distLabel ? <span className="pch-bid-panel__slot-dist">{distLabel} away</span> : null}
                  {clinic ? <span className="pch-bid-panel__slot-clinic">{clinic}</span> : null}
                  {String(s.clinicAddress || "").trim() ? (
                    <span className="pch-bid-panel__slot-addr">{String(s.clinicAddress).trim()}</span>
                  ) : null}
                </button>
                <div className="pch-bid-panel__slot-tools">
                  {maps ? (
                    <a
                      className="pch-bid-panel__icon-btn"
                      href={maps}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open in Maps"
                      aria-label="Open clinic location in Google Maps"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MapPin size={18} strokeWidth={2} aria-hidden />
                    </a>
                  ) : null}
                  {tel ? (
                    <a className="pch-bid-panel__icon-btn" href={tel} title="Call clinic" aria-label="Call clinic">
                      <Phone size={18} strokeWidth={2} aria-hidden />
                    </a>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );

  return (
    <>
    <div className={`pch-bid-panel${isCompact ? " pch-bid-panel--compact" : ""}`}>
      {appointment ? (
        <div className="pch-bid-panel__status">
          <div className={`pch-bid-panel__status-row${visitSectionOpen ? "" : " pch-bid-panel__status-row--visit-collapsed"}`}>
            <CalendarClock size={isCompact ? 16 : 18} strokeWidth={2} aria-hidden />
            <div className="pch-bid-panel__status-copy">
              <button
                type="button"
                className="pch-bid-panel__visit-toggle"
                aria-expanded={visitSectionOpen}
                aria-controls={visitDetailsId}
                id={visitSummaryId}
                onClick={() => setVisitSectionOpen((o) => !o)}
              >
                {appointmentStatus === "pending_approval" ? (
                  <div className="pch-bid-panel__visit-lead pch-bid-panel__visit-lead--toggle">
                    <strong>Visit</strong>
                    <p className="pch-bid-panel__visit-lead-msg">
                      {patientPendingAppointmentLeadMessage(appointment, providerLabel)}
                    </p>
                  </div>
                ) : appointmentStatus === "scheduled" ? (
                  <div className="pch-bid-panel__visit-lead pch-bid-panel__visit-lead--toggle">
                    <strong>Visit</strong>
                    <p className="pch-bid-panel__visit-lead-msg">
                      Scheduled: {appointmentTimePart(appointment) || "—"}
                      {appointment.appointmentDate
                        ? ` · ${new Date(appointment.appointmentDate).toLocaleDateString("en-IN")}`
                        : ""}
                    </p>
                  </div>
                ) : (
                  <div className="pch-bid-panel__visit-lead pch-bid-panel__visit-lead--toggle">
                    <strong>Visit</strong>
                    <p className="pch-bid-panel__visit-lead-msg">Status: {appointment.status || "—"}</p>
                  </div>
                )}
                <ChevronDown
                  size={isCompact ? 18 : 20}
                  strokeWidth={2.25}
                  className={`pch-bid-panel__visit-chevron${visitSectionOpen ? " pch-bid-panel__visit-chevron--open" : ""}`}
                  aria-hidden
                />
              </button>
              {visitSectionOpen ? (
                <div
                  id={visitDetailsId}
                  className="pch-bid-panel__visit-details"
                  role="region"
                  aria-labelledby={visitSummaryId}
                >
                  {appointmentStatus === "pending_approval" ? (
                    <div className="pch-bid-panel__visit-lines pch-bid-panel__visit-lines--stack">
                      <p>
                        Requested: {appointmentTimePart(appointment) || "—"}
                        {appointment.appointmentDate
                          ? ` · ${new Date(appointment.appointmentDate).toLocaleDateString("en-IN")}`
                          : ""}
                      </p>
                      {(appointment.doctorAction === "reschedule" ||
                        appointment.doctorAction === "custom") &&
                      appointment.doctorProposedDate &&
                      appointment.doctorProposedTimeSlot ? (
                        <p>
                          Proposed: {String(appointment.doctorProposedTimeSlot || "").trim() || "—"}
                          {" · "}
                          {new Date(appointment.doctorProposedDate).toLocaleDateString("en-IN")}
                        </p>
                      ) : null}
                      {appointmentClinicLabel(appointment) ? (
                        <p className="pch-bid-panel__visit-clinic">
                          Clinic / location: <strong>{appointmentClinicLabel(appointment)}</strong>
                        </p>
                      ) : null}
                    </div>
                  ) : appointmentStatus === "scheduled" && appointmentClinicLabel(appointment) ? (
                    <div className="pch-bid-panel__visit-lines pch-bid-panel__visit-lines--stack">
                      <p className="pch-bid-panel__visit-clinic">
                        Clinic / location: <strong>{appointmentClinicLabel(appointment)}</strong>
                      </p>
                    </div>
                  ) : null}
                  {visitPhone ? (
                    <p className="pch-bid-panel__visit-phone">
                      Clinic phone:{" "}
                      {visitTel ? (
                        <a href={visitTel} className="pch-bid-panel__tel">
                          {visitPhone}
                        </a>
                      ) : (
                        <span>{visitPhone}</span>
                      )}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
            {visitSectionOpen && (visitMapsUrl || visitTel) ? (
              <div className="pch-bid-panel__visit-tools">
                {visitMapsUrl ? (
                  <a
                    className="pch-bid-panel__icon-btn pch-bid-panel__icon-btn--visit"
                    href={visitMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open in Maps"
                    aria-label="Open clinic location in Google Maps"
                  >
                    <MapPin size={18} strokeWidth={2} aria-hidden />
                    <span className="pch-bid-panel__visit-tool-label">Map</span>
                  </a>
                ) : null}
                {visitTel ? (
                  <a
                    className="pch-bid-panel__icon-btn pch-bid-panel__icon-btn--visit"
                    href={visitTel}
                    title="Call clinic"
                    aria-label="Call clinic"
                  >
                    <Phone size={18} strokeWidth={2} aria-hidden />
                    <span className="pch-bid-panel__visit-tool-label">Call</span>
                  </a>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {needsPatientResponse ? (
        <div className={`pch-bid-panel__offer${isCompact ? "" : " pch-bid-panel__offer--respond"}`}>
          {isCompact ? (
            <>
              <div className="pch-bid-panel__compact-head">
                <span className="pch-bid-panel__compact-badge">Offer</span>
                {actionBid.providerAction === "countered" && actionBid.reverseBidAmount != null ? (
                  <span className="pch-bid-panel__compact-line">
                    {providerLabel} asks <strong>{fmtINR(actionBid.reverseBidAmount)}</strong>
                  </span>
                ) : (
                  <span className="pch-bid-panel__compact-line">
                    {providerLabel} accepted <strong>{fmtINR(actionBid.patientBidPrice)}</strong>
                  </span>
                )}
              </div>
              <div className="pch-bid-panel__actions pch-bid-panel__actions--tight">
                <button type="button" className="pch-bid-panel__btn pch-bid-panel__btn--primary" disabled={busy} onClick={() => void onAcceptOffer()}>
                  Accept
                </button>
                <button type="button" className="pch-bid-panel__btn pch-bid-panel__btn--muted" disabled={busy} onClick={() => void onDeclineOffer()}>
                  Decline
                </button>
              </div>
              {showCounter ? (
                <div className="pch-bid-panel__counter pch-bid-panel__counter--inline">
                  <label className="pch-bid-panel__sr-only" htmlFor="pch-counter-amt">
                    Counter in Rs
                  </label>
                  <div className="pch-bid-panel__counter-pill">
                    <input
                      id="pch-counter-amt"
                      type="text"
                      inputMode="numeric"
                      placeholder="Your counter (Rs)"
                      value={counter}
                      onChange={(e) => onCounterFieldChange(e.target.value)}
                      disabled={busy}
                    />
                    <button
                      type="button"
                      className="pch-bid-panel__send-round"
                      disabled={busy || !String(counter).trim()}
                      onClick={() => void onSubmitCounter()}
                      aria-label="Send counter-offer"
                    >
                      <Send size={18} strokeWidth={2} aria-hidden />
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className="pch-bid-panel__respond-head">
                <div className="pch-bid-panel__respond-topline">
                  <span className="pch-bid-panel__respond-badge">Your move</span>
                  <p className="pch-bid-panel__respond-lead">
                    {actionBid.providerAction === "countered" && actionBid.reverseBidAmount != null
                      ? `${providerLabel} offered a new price.`
                      : `${providerLabel} accepted your bid.`}
                  </p>
                </div>
                <p className="pch-bid-panel__respond-price" aria-live="polite">
                  {fmtINR(
                    actionBid.providerAction === "countered" && actionBid.reverseBidAmount != null
                      ? actionBid.reverseBidAmount
                      : actionBid.patientBidPrice
                  )}
                </p>
                <p className="pch-bid-panel__respond-meta">
                  {actionBid.providerAction === "countered" && actionBid.reverseBidAmount != null
                    ? "Accept, decline, or counter."
                    : "Confirm to lock in and book."}
                </p>
              </div>
              <div className="pch-bid-panel__respond-actions">
                <button type="button" className="pch-bid-panel__btn pch-bid-panel__btn--primary" disabled={busy} onClick={() => void onAcceptOffer()}>
                  Accept
                </button>
                <button type="button" className="pch-bid-panel__btn pch-bid-panel__btn--muted" disabled={busy} onClick={() => void onDeclineOffer()}>
                  Decline
                </button>
              </div>
              {showCounter ? (
                <div className="pch-bid-panel__respond-counter">
                  <label className="pch-bid-panel__sr-only" htmlFor="pch-counter-amt-hub">
                    Counter-offer amount in Rs
                  </label>
                  <div className="pch-bid-panel__respond-composer" role="group" aria-label="Counter-offer">
                    <span className="pch-bid-panel__respond-composer-prefix">Rs</span>
                    <input
                      id="pch-counter-amt-hub"
                      type="text"
                      inputMode="numeric"
                      placeholder="Counter"
                      autoComplete="off"
                      aria-label="Counter-offer amount in Rs"
                      value={counter}
                      onChange={(e) => onCounterFieldChange(e.target.value)}
                      disabled={busy}
                    />
                    <button
                      type="button"
                      className="pch-bid-panel__respond-composer-send"
                      disabled={busy || !String(counter).trim()}
                      onClick={() => void onSubmitCounter()}
                    >
                      Send
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}

      {!needsPatientResponse && !canBook && !appointment && waitingOpeningBid && openingBidExpired ? (
        <div
          className="pch-bid-panel__waiting pch-bid-panel__waiting--compact pch-bid-panel__waiting--expired"
          role="status"
        >
          <div className="pch-bid-panel__waiting-iconwrap pch-bid-panel__waiting-iconwrap--expired" aria-hidden>
            <AlertCircle size={20} strokeWidth={2} />
          </div>
          <div className="pch-bid-panel__waiting-copy">
            <p className="pch-bid-panel__waiting-title">Offer window ended</p>
            <p className="pch-bid-panel__waiting-sub">
              Your opening amount was shared, but the time allowed for a first reply from this {providerLabel.toLowerCase()}{" "}
              has passed without a response. You can still read this thread for your records; new actions may require a
              fresh request or support.
            </p>
          </div>
        </div>
      ) : null}

      {!needsPatientResponse &&
      !canBook &&
      !appointment &&
      ((waitingOpeningBid && !openingBidExpired) || waitingAfterPatientCounter) ? (
        <div className="pch-bid-panel__waiting pch-bid-panel__waiting--compact" role="status">
          {waitingOpeningBid && !openingBidExpired ? (
            <>
              <div className="pch-bid-panel__waiting-iconwrap" aria-hidden>
                <Clock size={20} strokeWidth={2} />
              </div>
              <div className="pch-bid-panel__waiting-copy">
                <p className="pch-bid-panel__waiting-title">Waiting on your {providerLabel.toLowerCase()}</p>
                <p className="pch-bid-panel__waiting-sub">
                  They review your opening bid from their side. There’s nothing to tap here yet—when they respond with
                  a price (or accept yours), real <strong>Accept</strong>, <strong>Counter</strong>, and{" "}
                  <strong>Decline</strong> buttons will replace this message in this panel.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="pch-bid-panel__waiting-iconwrap" aria-hidden>
                <Send size={20} strokeWidth={2} />
              </div>
              <div className="pch-bid-panel__waiting-copy">
                <p className="pch-bid-panel__waiting-title">Counter with your {providerLabel.toLowerCase()}</p>
                <p className="pch-bid-panel__waiting-sub">They’ll accept, counter, or decline—this thread updates live.</p>
              </div>
            </>
          )}
        </div>
      ) : null}

      {canBook && !appointment ? (
        <div className="pch-bid-panel__book">
          {!bookOpen ? (
            <button
              type="button"
              className="pch-bid-panel__btn pch-bid-panel__btn--block"
              disabled={slotsLoading}
              onClick={() => {
                if (narrowBookSheet && !bookingUiInFocusedThread) {
                  onRequestOpenMobileThread?.();
                }
                setInlineError("");
                setBookOpen(true);
              }}
            >
              {slotsLoading ? "Loading times…" : "Book visit"}
            </button>
          ) : !narrowBookSheet ? (
            <div className="pch-bid-panel__book-body">{renderBookingVisitInner()}</div>
          ) : null}
        </div>
      ) : canChangePendingSlot ? (
        <div className="pch-bid-panel__book">
          {!bookOpen ? (
            <button
              type="button"
              className="pch-bid-panel__btn pch-bid-panel__btn--block"
              disabled={slotsLoading}
              onClick={() => {
                if (narrowBookSheet && !bookingUiInFocusedThread) {
                  onRequestOpenMobileThread?.();
                }
                setInlineError("");
                setBookOpen(true);
              }}
            >
              {slotsLoading ? "Loading times…" : "Change visit time"}
            </button>
          ) : !narrowBookSheet ? (
            <div className="pch-bid-panel__book-body">{renderBookingVisitInner()}</div>
          ) : null}
        </div>
      ) : null}

      {inlineError && !bookingSheetActive ? (
        <p className="pch-bid-panel__err" role="alert">
          {inlineError}
        </p>
      ) : null}
    </div>

    {bookingSheetActive
      ? createPortal(
          <div className="pch-book-sheet" role="dialog" aria-modal="true" aria-labelledby="pch-book-sheet-title">
            <div
              className="pch-book-sheet__backdrop"
              role="presentation"
              onClick={() => {
                if (Date.now() < sheetBackdropSuppressUntilRef.current) return;
                if (!busy) void onCloseBooking();
              }}
            />
            <div className="pch-book-sheet__sheet" role="document" onClick={(e) => e.stopPropagation()}>
              <div className="pch-book-sheet__handle" aria-hidden />
              <div className="pch-book-sheet__scroll">
                <div className="pch-bid-panel__book-body pch-book-sheet__inner">{renderBookingVisitInner()}</div>
              </div>
            </div>
          </div>,
          document.body
        )
      : null}

    {confirmSlot ? (
      <div
        className="pch-bid-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pch-slot-confirm-title"
      >
        <div
          className="pch-bid-modal__overlay"
          role="presentation"
          onClick={() => {
            if (!busy) setSlotConfirmOpen(null);
          }}
        />
        <div
          className="pch-bid-modal__card"
          role="document"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="pch-bid-modal__head">
            <h2 id="pch-slot-confirm-title" className="pch-bid-modal__title">
              {canChangePendingSlot ? "Update visit time" : "Book this visit"}
            </h2>
            <button
              type="button"
              className="pch-bid-modal__close"
              disabled={busy}
              aria-label="Close"
              onClick={() => setSlotConfirmOpen(null)}
            >
              <X size={20} strokeWidth={2} aria-hidden />
            </button>
          </div>
          <p className="pch-bid-modal__intro">
            {canChangePendingSlot
              ? `You are replacing your pending request with another published slot. Your ${providerLabel.toLowerCase()} will see the update in this thread.`
              : `You are requesting this visit at the agreed price. Your ${providerLabel.toLowerCase()} will see it as a booking request. If anything clashes, our team will reach out.`}
          </p>

          <dl className="pch-bid-modal__dl">
            <div className="pch-bid-modal__row">
              <dt>Agreed price</dt>
              <dd>
                <strong className="pch-bid-modal__price">{agreedPriceLabel}</strong>
              </dd>
            </div>
            <div className="pch-bid-modal__row">
              <dt>Procedure</dt>
              <dd>{procedureLabel || "—"}</dd>
            </div>
            <div className="pch-bid-modal__row">
              <dt>With</dt>
              <dd>{providerLabel}</dd>
            </div>
            <div className="pch-bid-modal__row">
              <dt>Date & time</dt>
              <dd>
                {formatSlotCalendar(confirmSlot.appointmentDate) || confirmSlot.appointmentDate} ·{" "}
                {slotTimePart(confirmSlot)}
              </dd>
            </div>
            {confirmDistLabel ? (
              <div className="pch-bid-modal__row">
                <dt>Distance</dt>
                <dd>
                  {confirmDistLabel} away <span className="pch-bid-modal__muted">(straight-line)</span>
                </dd>
              </div>
            ) : null}
            <div className="pch-bid-modal__row">
              <dt>Clinic</dt>
              <dd>
                {confirmClinic || "—"}
                {String(confirmSlot.clinicAddress || "").trim() ? (
                  <>
                    <br />
                    <span className="pch-bid-modal__muted">{String(confirmSlot.clinicAddress).trim()}</span>
                  </>
                ) : null}
                {confirmPhone ? (
                  <>
                    <br />
                    <span className="pch-bid-modal__muted">
                      Phone:{" "}
                      {confirmTel ? (
                        <a href={confirmTel} className="pch-bid-modal__link">
                          {confirmPhone}
                        </a>
                      ) : (
                        confirmPhone
                      )}
                    </span>
                  </>
                ) : null}
                {confirmMaps ? (
                  <>
                    <br />
                    <a
                      className="pch-bid-modal__link"
                      href={confirmMaps}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open location in Maps
                    </a>
                  </>
                ) : null}
              </dd>
            </div>
          </dl>

          <div className="pch-bid-modal__actions">
            <button
              type="button"
              className="pch-bid-panel__btn"
              disabled={busy}
              onClick={() => setSlotConfirmOpen(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="pch-bid-panel__btn pch-bid-panel__btn--primary"
              disabled={busy}
              onClick={() => void onConfirmBookFromModal()}
            >
              {busy ? (canChangePendingSlot ? "Updating…" : "Booking…") : canChangePendingSlot ? "Confirm new time" : "Book this visit"}
            </button>
          </div>
        </div>
      </div>
    ) : null}
    </>
  );
}
